import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ethers } from "ethers";
import { configAbi, merchantRegistryAbi } from "@/abi";
import { useSmartWallet } from "./SmartWallet";
import { LocalStorageKeys } from "@/constants";

interface MerchantDetails {
  stake: number;
  isOnline: boolean;
  isApproved: boolean;
  isRegistered: boolean;
  freeAmountFiat: number;
}

interface MerchantConfig {
  minStake: number;
  leverage: number;
  tier: number;
  redactedAccNum: number;
}

interface IMerchantContext {
  merchantDetails: MerchantDetails | undefined;
  merchantConfig: MerchantConfig | undefined;
  sellPrice: number;
  amountForUnstake: number;
  feeAccrued: number;
  isLoading: boolean;
  error: Error | null;
  hasFetched: boolean;
  refreshMerchant: () => Promise<void>;
  freeAmountFiat: number;
  redactedAccNum: number;
  unstakedRequested: boolean;
  merchantBank: string | null;
  setMerchantBankName: (name: string) => void;
}

const initialMerchantContext: IMerchantContext = {
  merchantDetails: undefined,
  merchantConfig: undefined,
  sellPrice: 0,
  amountForUnstake: 0,
  feeAccrued: 0,
  isLoading: false,
  error: null,
  hasFetched: false,
  refreshMerchant: async () => undefined,
  freeAmountFiat: 0,
  redactedAccNum: 0,
  unstakedRequested: false,
  merchantBank: null,
  setMerchantBankName: () => undefined,
};
const ethersProvider = new ethers.providers.JsonRpcProvider(
  import.meta.env.VITE_RPC_URL,
);
const readContract = async (
  contractAddress: string,
  contractABI: any[],
  functionName: string,
  args: any[] = [],
) => {
  try {
    const contractInstance = new ethers.Contract(
      contractAddress,
      contractABI,
      ethersProvider,
    );
    const result = await contractInstance[functionName](...args);
    console.log(result, "--", functionName);
    return result;
  } catch (err: any) {
    console.log("check errrrrr---", functionName, args, err);
  }
};

const MerchantContext = createContext(initialMerchantContext);

export const MerchantContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { smartWalletAddress } = useSmartWallet();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [merchantDetails, setMerchantDetails] = useState<MerchantDetails>();
  const [merchantConfig, setMerchantConfig] = useState<MerchantConfig>();
  const [sellPrice, setSellPrice] = useState(0);
  const [amountForUnstake, setAmountForUnstake] = useState(0);
  const [unstakedRequested, setUnstakedRequested] = useState(false);
  const [feeAccrued, setFeeAccrued] = useState(0);
  const [merchantBank, setMerchantBank] = useState<string | null>(null);

  const readMerchantDetailsAndConfig = async () => {
    setIsLoading(true);
    try {
      const merchantDeets = await readContract(
        import.meta.env.VITE_MERCHANT_REGISTRY,
        merchantRegistryAbi,
        "merchantDetails",
        [smartWalletAddress],
      );
      const merchantCfg = await readContract(
        import.meta.env.VITE_MERCHANT_REGISTRY,
        merchantRegistryAbi,
        "merchantConfig",
        [smartWalletAddress],
      );
      const priceConfig = await readContract(
        import.meta.env.VITE_CONFIG_CONTRACT,
        configAbi,
        "priceConfig",
        [ethers.utils.formatBytes32String("INR")],
      );

      console.log(smartWalletAddress, "smartWalletAddress");
      const unstakedRequested = await readContract(
        import.meta.env.VITE_MERCHANT_REGISTRY,
        merchantRegistryAbi,
        "unstakeRequested",
        [smartWalletAddress],
      );

      const amountForUnstake = await readContract(
        import.meta.env.VITE_MERCHANT_REGISTRY,
        merchantRegistryAbi,
        "amountForUnstake",
        [smartWalletAddress],
      );

      const feeAccrued = await readContract(
        import.meta.env.VITE_MERCHANT_REGISTRY,
        merchantRegistryAbi,
        "feeAccrued",
        [smartWalletAddress],
      );

      if (merchantDeets && merchantCfg && priceConfig?.sellPrice) {
        const merchantDetails = {
          stake: Number(ethers.utils.formatUnits(merchantDeets.stake, 6)),
          isOnline: merchantDeets.isOnline,
          isApproved: merchantDeets.isApproved,
          isRegistered: merchantDeets.isRegistered,
          freeAmountFiat: Number(
            ethers.utils.formatUnits(merchantDeets?.freeAmount?.fiat, 6),
          ),
        };

        localStorage.setItem(
          LocalStorageKeys.MERCHANT_DETAILS,
          JSON.stringify(merchantDetails),
        );
        setAmountForUnstake(amountForUnstake);
        setUnstakedRequested(unstakedRequested);
        setSellPrice(
          Number(ethers.utils.formatUnits(priceConfig?.sellPrice, 6)),
        );
        setMerchantDetails(merchantDetails);
        setMerchantConfig({
          minStake: Number(ethers.utils.formatUnits(merchantCfg.minStake, 6)),
          leverage: Number(ethers.utils.formatUnits(merchantCfg.leverage, 6)),
          tier: merchantCfg.tier,
          redactedAccNum: Number(merchantCfg.redactedAccNum),
        });
        setError(null);
        setHasFetched(true);
        setFeeAccrued(Number(ethers.utils.formatUnits(feeAccrued, 6)));
      } else {
        setError(
          new Error(
            "Either or both of merchantDeets or merchantCfg were undefined.",
          ),
        );
        console.error(
          "Either or both of merchantDeets or merchantCfg were undefined.",
        );
      }
    } catch (error) {
      setError(error as Error);
      console.error("readingMerchantDetailsAndConfig in useMerchant", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (smartWalletAddress) void readMerchantDetailsAndConfig();
  }, [smartWalletAddress]);

  useEffect(() => {
    (async () => {
      const _merchBank = await localStorage.getItem(
        LocalStorageKeys.MERCH_BANK,
      );
      if (_merchBank) {
        setMerchantBank(_merchBank);
      }
    })();
  }, []);

  const setMerchantBankName = (name: string) => {
    setMerchantBank(name);
    localStorage.setItem(LocalStorageKeys.MERCH_BANK, name);
  };

  return (
    <MerchantContext.Provider
      // @ts-ignore
      value={{
        merchantDetails,
        merchantConfig,
        sellPrice,
        amountForUnstake,
        feeAccrued,
        isLoading,
        error,
        hasFetched,
        unstakedRequested,
        refreshMerchant: readMerchantDetailsAndConfig,
        merchantBank,
        setMerchantBankName,
      }}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = () => useContext(MerchantContext);
